import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Avatar, Button, Card, Descriptions, Empty, Skeleton, Space, Statistic, Tag, Typography,
} from 'antd';
import { ArrowLeftOutlined, MessageOutlined, PhoneOutlined } from '@ant-design/icons';
import type { GuardianDetailReceipt, GuardianDetailWard } from '@velora/contracts';
import { productApi } from '../../../api';

/**
 * Admin — Guardian record. Aggregate read: profile + wards + open dues per
 * ward + recent receipts across all wards + broadcast/message counters.
 */
export function GuardianDetailPage() {
  const { institution = '', code = '' } = useParams<{ institution: string; code: string }>();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['guardian-detail', institution, code],
    queryFn: () => productApi.getGuardianDetail(institution, code),
    enabled: !!institution && !!code,
  });

  if (q.isLoading || !q.data) return <Skeleton active paragraph={{ rows: 8 }} />;
  const d = q.data;

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Button icon={<ArrowLeftOutlined />} type="text" size="small" onClick={() => navigate(`/i/${institution}/admin/guardians`)}>Back</Button>
      </div>

      <Card size="small">
        <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
          <Avatar size={56} style={{ background: `hsl(${(d.guardian.name.length * 37) % 360}, 40%, 55%)`, fontSize: 18 }}>
            {d.guardian.name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase()}
          </Avatar>
          <div style={{ flex: 1 }}>
            <Typography.Title level={3} style={{ margin: 0 }}>{d.guardian.name}</Typography.Title>
            <div style={{ color: '#5b6478', fontSize: 12, fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>
              <PhoneOutlined /> {d.guardian.phone}
              {d.guardian.email && <> · {d.guardian.email}</>}
            </div>
          </div>
          <Descriptions size="small" column={1} contentStyle={{ fontSize: 12 }} labelStyle={{ fontSize: 12, color: '#8a94a6' }}>
            <Descriptions.Item label="Wards">{d.wards.length}</Descriptions.Item>
            <Descriptions.Item label="Threads">{d.message_threads}</Descriptions.Item>
            <Descriptions.Item label="Broadcasts">{d.broadcasts_received}</Descriptions.Item>
          </Descriptions>
        </div>
      </Card>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        <Card size="small"><Statistic title="Billed" value={`₹${paise(d.totals.billed_paise)}`} valueStyle={{ fontSize: 18 }} /></Card>
        <Card size="small"><Statistic title="Paid" value={`₹${paise(d.totals.paid_paise)}`} valueStyle={{ color: '#1c6442', fontSize: 18 }} /></Card>
        <Card size="small"><Statistic title="Owed" value={`₹${paise(d.totals.open_paise)}`} valueStyle={{ color: d.totals.open_paise > 0 ? '#b3382f' : '#1c6442', fontSize: 18 }} /></Card>
      </div>

      <Card size="small" title={`Wards (${d.wards.length})`}>
        {d.wards.length === 0
          ? <Empty description="Not linked to any student yet." style={{ padding: 20 }} />
          : (
            <div style={{ display: 'grid', gap: 8 }}>
              {d.wards.map((w) => <WardRow key={w.student_id} w={w} institution={institution} />)}
            </div>
          )}
      </Card>

      <Card size="small" title={`Recent receipts (${d.recent_receipts.length})`}>
        {d.recent_receipts.length === 0
          ? <Empty description="No receipts yet." style={{ padding: 20 }} />
          : (
            <div style={{ display: 'grid', gap: 6 }}>
              {d.recent_receipts.map((r) => <ReceiptRow key={r.receipt_number} r={r} />)}
            </div>
          )}
      </Card>

      <Alert
        type="info" showIcon
        message="Rights & payment history"
        description="Per-guardian consent history and detailed payment audit live at the Comms/Consent module — coming later."
        action={<Link to={`/i/${institution}/admin/comms/messages`}><Button icon={<MessageOutlined />} size="small">Open messages</Button></Link>}
      />
    </Space>
  );
}

function WardRow({ w, institution }: { w: GuardianDetailWard; institution: string }) {
  return (
    <Link to={`/i/${institution}/admin/students/${encodeURIComponent(w.admission_number)}`} style={{ textDecoration: 'none' }}>
      <div style={{
        border: '1px solid #eef0f3', borderRadius: 6, padding: '10px 12px',
        display: 'flex', gap: 10, alignItems: 'center', background: '#fff',
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: '50%',
          background: '#eaf3fb', color: '#2f6ab0',
          display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700,
        }}>{w.name.charAt(0).toUpperCase()}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, color: '#14213d' }}>{w.name}</div>
          <div style={{ fontSize: 11.5, color: '#5b6478' }}>
            <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{w.admission_number}</span>
            {' · '}{w.grade}{w.section ? `-${w.section}` : ''}
            {' · '}{w.relation}{w.is_primary ? ' · primary' : ''}
          </div>
        </div>
        {w.status !== 'enrolled' && <Tag>{w.status}</Tag>}
        {w.open_paise > 0 && <Tag color="error">₹{paise(w.open_paise)} due</Tag>}
      </div>
    </Link>
  );
}

function ReceiptRow({ r }: { r: GuardianDetailReceipt }) {
  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', fontSize: 12.5, padding: '4px 0', borderTop: '1px solid #f1f1f4' }}>
      <span style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}>{r.receipt_number}</span>
      <span>{r.received_on}</span>
      <span style={{ color: '#5b6478' }}>{r.student_name}</span>
      <span style={{ color: '#5b6478' }}>· {r.fee_head_name}</span>
      <div style={{ flex: 1 }} />
      <b>₹{paise(r.amount_paise)}</b>
      <Tag style={{ margin: 0 }}>{r.method}</Tag>
    </div>
  );
}

function paise(n: number): string {
  return (n / 100).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}
